/**
 * DealActivityTimeline - Timeline de atividades do Deal
 * Exibe histórico completo de atividades com visual diferenciado
 * Versão: 1.0.0
 */
import React from 'react';
import { format } from 'date-fns';
import { Clock as ClockIcon } from 'lucide-react';
import { ptBR } from 'date-fns/locale';
import { 
  FileText, 
  Phone, 
  Mail, 
  Users, 
  CheckSquare,
  ArrowRight,
  DollarSign,
  Flag,
  UserPlus,
  Plus,
  Package,
  PackageMinus,
  Circle,
  Trash2,
  CheckCircle2,
  FileSignature,
  CreditCard
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCrmDealActivities, ActivityType } from '@/hooks/useCrmDealActivities';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// Mapeamento de ícones por tipo de atividade
const activityIcons: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  call: Phone,
  email: Mail,
  meeting: Users,
  task: CheckSquare,
  phase_change: ArrowRight,
  value_change: DollarSign,
  status_change: Flag,
  assignment: UserPlus,
  created: Plus,
  product_added: Package,
  product_removed: PackageMinus,
  contract_generated: FileSignature,
  charge_generated: CreditCard,
};

// Configuração de cores por tipo
const activityColors: Record<ActivityType, string> = {
  note: 'bg-blue-500 text-white',
  call: 'bg-green-500 text-white',
  email: 'bg-purple-500 text-white',
  meeting: 'bg-orange-500 text-white',
  task: 'bg-yellow-500 text-white',
  phase_change: 'bg-muted text-muted-foreground',
  value_change: 'bg-muted text-muted-foreground',
  status_change: 'bg-muted text-muted-foreground',
  assignment: 'bg-muted text-muted-foreground',
  created: 'bg-muted text-muted-foreground',
  product_added: 'bg-muted text-muted-foreground',
  product_removed: 'bg-muted text-muted-foreground',
  contract_generated: 'bg-violet-500 text-white',
  charge_generated: 'bg-emerald-500 text-white',
};

// Props do componente
interface DealActivityTimelineProps {
  dealId: string;
  maxItems?: number;
}

/**
 * Timeline de atividades do Deal
 */
export function DealActivityTimeline({ 
  dealId, 
  maxItems 
}: DealActivityTimelineProps) {
  const { 
    activities, 
    isLoading, 
    completeActivity, 
    deleteActivity 
  } = useCrmDealActivities(dealId);

  // Limita itens se especificado
  const displayActivities = maxItems 
    ? activities.slice(0, maxItems) 
    : activities;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Nenhuma atividade registrada.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Linha vertical conectando os items */}
      <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

      <div className="space-y-4">
      {displayActivities.map((activity, index) => {
          const Icon = activityIcons[activity.activity_type] || Circle;
          const colorClass = activityColors[activity.activity_type] || 'bg-muted text-muted-foreground';
          const isLast = index === displayActivities.length - 1;

          return (
            <div 
              key={activity.id} 
              className="relative flex gap-3 group"
            >
              {/* Ícone com cor */}
              <div 
                className={cn(
                  'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-background',
                  colorClass
                )}
              >
                <Icon className="h-4 w-4" />
              </div>

              {/* Conteúdo */}
              <div className="flex-1 min-w-0 pb-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Título e badge de tipo */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {activity.title}
                      </span>
                      {activity.is_system && (
                        <Badge variant="outline" className="text-xs">
                          Sistema
                        </Badge>
                      )}
                    </div>

                    {/* Descrição com suporte a HTML (ex: agendamentos com <br>) */}
                    {activity.description && (
                      <p 
                        className="text-sm text-muted-foreground mt-1"
                        dangerouslySetInnerHTML={{ __html: activity.description }}
                      />
                    )}

                    {/* Detalhes enriquecidos para cobrança gerada */}
                    {activity.activity_type === 'charge_generated' && activity.new_value && (() => {
                      const nv = activity.new_value as Record<string, any>;
                      return (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                          <DollarSign className="h-3 w-3" />
                          R$ {Number(nv.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Badge>
                        {Number(nv.numero_parcelas) > 1 && (
                          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                            {nv.numero_parcelas}x parcelas
                          </Badge>
                        )}
                        {nv.tipo_pagamento && nv.tipo_pagamento !== 'N/A' && (
                          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                            {({
                              pix: 'PIX', boleto: 'Boleto', cartao_credito: 'Cartão Crédito',
                              cartao_debito: 'Cartão Débito', indefinido: 'Pergunte ao Cliente',
                              dinheiro: 'Dinheiro', ted: 'TED', doc: 'DOC',
                              carne: 'Carnê', cheque: 'Cheque', nota_promissoria: 'Nota Promissória',
                            } as Record<string, string>)[String(nv.tipo_pagamento)] || String(nv.tipo_pagamento)}
                          </Badge>
                        )}
                        {nv.data_vencimento && (
                          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                            <ClockIcon className="h-3 w-3" />
                            Venc. {format(new Date(nv.data_vencimento + 'T00:00:00'), 'dd/MM/yyyy')}
                          </Badge>
                        )}
                      </div>
                      );
                    })()}

                    {/* Metadados */}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>
                        {format(new Date(activity.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                      {activity.performer && (
                        <>
                          <span>•</span>
                          <span>{activity.performer.full_name}</span>
                        </>
                      )}
                      {activity.activity_type !== 'note' && activity.scheduled_at && !activity.completed_at && (
                        <>
                          <span>•</span>
                          <span className="text-orange-600">
                            Agendado: {format(new Date(activity.scheduled_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                          </span>
                        </>
                      )}
                      {activity.activity_type !== 'note' && activity.completed_at && (
                        <>
                          <span>•</span>
                          <span className="text-green-600">
                            Concluído
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Ações para atividades manuais */}
                  {!activity.is_system && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {activity.activity_type !== 'note' && !activity.completed_at && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => completeActivity.mutate(activity.id)}
                          title="Marcar como concluído"
                        >
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => deleteActivity.mutate(activity.id)}
                        title="Excluir atividade"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Indicador de mais atividades */}
      {maxItems && activities.length > maxItems && (
        <div className="text-center pt-2">
          <span className="text-sm text-muted-foreground">
            + {activities.length - maxItems} atividades
          </span>
        </div>
      )}
    </div>
  );
}
